<template>
  <div class="microbiology">
    <div class="microbiology__subheader width-responsiveness">
      <h2>Parcial Microbiologia</h2>
    </div>
    <MicrobiologyFilter @search="handleSearch" />

    <MicrobiologyResults
      v-if="hasSearched"
      :results="results"
      :loading="loading"
    />
  </div>
</template>

<script>
import MicrobiologyFilter from './MicrobiologyFilter.vue'
import MicrobiologyResults from './MicrobiologyResults.vue'
import microbiologyApi from '@/api/microbiology'
import notify from '@/api/notification'
import moment from 'moment'

export default {
  name: 'Microbiology',
  components: {
    MicrobiologyFilter,
    MicrobiologyResults,
  },
  data() {
    return {
      results: [],
      loading: false,
      hasSearched: false,
    }
  },
  methods: {
    async handleSearch(filters) {
      this.loading = true
      this.hasSearched = true

      try {
        let results = []
        const response = await microbiologyApi.searchMicrobiologyResults(filters)
        results = this.transformApiResponse(response.data)
        if (filters.onlyPositive) {
          results = results.filter(r =>
            (r.finalResult || r.finalresult || '').toLowerCase().includes('positivo')
          )
        }
        this.results = results
      } catch (error) {
        console.error('Erro ao buscar resultados:', error)
        notify({
          title: 'ERRO:',
          message: 'Erro ao buscar resultados. Tente novamente.',
        })
        this.results = []
      } finally {
        this.loading = false
      }
    },

    transformApiResponse(apiData) {
      if (!apiData.content || !Array.isArray(apiData.content)) {
        return []
      }

      return apiData.content
        .map(order => {
          if (!order.exams || !Array.isArray(order.exams)) {
            return []
          }

          return order.exams.map(exam => ({
            id: `${order.orderId || 'unknown'}_${
              exam.examRequestId || exam.id || Math.random()
            }`,
            orderId: order.orderId || '',
            orderIdLis: order.orderIdLis || order.orderLisId || exam.orderLisId || 'N/A',
            patient: order.patient || 'Nome não disponível',
            material: exam.material || 'Material não especificado',
            exam: exam.name || 'Exame não especificado',
            examCode: exam.code || '',
            sample: exam.sampleId || 'N/A',
            requestDate: order.registrationDate
              ? moment(order.registrationDate).format('DD/MM/YYYY - HH:mm')
              : 'Data não disponível',
            collectionDate: order.collectionDate
              ? moment(order.collectionDate).format('DD/MM/YYYY - HH:mm')
              : 'Data não disponível',
            status: exam.finalized ? 'Finalizado' : 'Em andamento',
            sitio: this.getSitioFromMaterial(exam.material || ''),
            progress: this.transformProgress(exam),
          }))
        })
        .flat()
    },

    transformProgress(exam) {
      const progressSteps = []

      if (exam.registrationDate) {
        progressSteps.push({
          date: moment(exam.registrationDate).format('DD MMM'),
          time: moment(exam.registrationDate).format('HH:mm'),
          year: moment(exam.registrationDate).format('YYYY'),
          step: 'Início',
          description: 'Recepção das amostras',
          status: 'completed',
        })
      }

      if (exam.timeline && Array.isArray(exam.timeline)) {
        exam.timeline.forEach((timelineItem, index) => {
          if (timelineItem.events && Array.isArray(timelineItem.events)) {
            timelineItem.events.forEach((event, eventIndex) => {
              if (event.eventDate) {
                const eventMoment = moment(event.eventDate)
                const isPartial =
                  timelineItem.status &&
                  timelineItem.status.toLowerCase().includes('parcial')
                const isFinal =
                  timelineItem.status &&
                  timelineItem.status.toLowerCase().includes('final')

                progressSteps.push({
                  date: eventMoment.format('DD MMM'),
                  time: eventMoment.format('HH:mm'),
                  year: eventMoment.format('YYYY'),
                  step: timelineItem.status || `${index + 1}ª Etapa`,
                  description: isPartial
                    ? 'PARCIAL'
                    : isFinal
                      ? 'RESULTADO FINAL'
                      : 'ETAPA',
                  subDescription: event.result || '',
                  additionalInfo:
                    isPartial && this.hasPartialAntibiogram(event)
                      ? 'Antibiograma parcial'
                      : isFinal && exam.finalized
                        ? 'Resultado final disponivel'
                        : '',
                  partialAntibiogram:
                    isPartial && this.hasPartialAntibiogram(event)
                      ? this.buildPartialAntibiogram(event, eventMoment)
                      : null,
                  status: isFinal ? 'final' : 'completed',
                })
              }
            })
          }
        })
      }

      return progressSteps
    },

    hasPartialAntibiogram(event) {
      return !!(
        event &&
        (event.partialAntibiogram ||
          event.antibiogram ||
          event.antibiogramData ||
          event.partialResult)
      )
    },

    buildPartialAntibiogram(event, eventMoment) {
      const data =
        event.partialAntibiogram || event.antibiogram || event.antibiogramData || {}
      const rows = Array.isArray(data.rows)
        ? data.rows
        : Array.isArray(data.items)
          ? data.items.map(item => ({
              antibiogram: item.antibiogram || item.name || '-',
              result: item.result || '-',
              cim: item.cim || item.cimValue || '-',
            }))
          : []

      return {
        microorganism:
          data.microorganism || event.result || 'Microorganismo nao informado',
        reportDate: data.reportDate || eventMoment.format('DD MMM YYYY - HH[h]mm'),
        unit: data.unit || 'ug/ml',
        rows,
        footerNote: data.footerNote || '*Antibiotico em revisao com o laboratorio',
        notes: Array.isArray(data.notes) ? data.notes : [],
      }
    },

    getSitioFromMaterial(material) {
      const sitioMap = {
        urina: 'Trato urinário',
        sangue: 'Sistema circulatório',
        secrecao: 'Trato respiratório',
        cateter: 'Acesso vascular',
        swab: 'Superfície corporal',
      }

      const materialLower = material.toLowerCase()
      for (const [key, value] of Object.entries(sitioMap)) {
        if (materialLower.includes(key)) {
          return value
        }
      }

      return 'Não especificado'
    },
  },
}
</script>

<style lang="scss" scoped>
.microbiology {
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  margin-top: 100px;
  font-family: 'Exo 2', sans-serif !important;
  padding-bottom: 40px;

  &__subheader {
    display: flex;
    justify-content: space-between;
    align-content: center;
    z-index: 1;

    h2 {
      font-size: 22px;
      font-weight: 600;
      letter-spacing: -0.3px;
      text-align: left;
      color: #7a7a7a;
      line-height: 30px;
    }
  }
}
</style>
